import React, { useState, useMemo } from 'react';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Organization, Person } from '../types';
import { Building2, Globe, MapPin, Briefcase, Users, ArrowLeft, ExternalLink, StickyNote, Edit3, ChevronRight, UserCircle, Tag, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { formatDateSafe } from '../lib/utils';
import { OrganizationFormModal } from '../features/organizations/OrganizationFormModal';
import { OrganizationDeleteDialog } from '../features/organizations/OrganizationDeleteDialog';

const { useParams, useNavigate } = ReactRouterDom as any;

interface Involvement {
  id: string;
  program: string;
  engagement: string;
  session: string | null;
  role: string;
  date: string | null;
  type: 'engagement' | 'session';
  linkId: string;
}

const formatAddressLines = (org: Organization) => {
  const lines = [];
  if (org.street_address) lines.push(org.street_address);
  const cityStateZip = [org.city, org.state, org.zip_code].filter(Boolean).join(' ');
  if (cityStateZip) lines.push(cityStateZip);
  return lines;
};

const OrganizationDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'involvement' | 'people'>('involvement');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: org, isLoading: loadingOrg } = useQuery({
    queryKey: ['organization-base', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*, organization_types(label)')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as Organization;
    }
  });

  const { data: engagements } = useQuery({
    queryKey: ['org-engagements', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engagement_organizations')
        .select(`
          engagement_id, 
          role_id, 
          engagements(id, name, programs(name)), 
          organization_roles(label)
        `)
        .eq('organization_id', id);
      if (error) throw error;
      return data || [];
    }
  });

  const { data: hostedSessions } = useQuery({
    queryKey: ['org-sessions', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sessions')
        .select('id, session_datetime, name, engagements(id, name, programs(name))')
        .eq('host_org_id', id);
      if (error) throw error;
      return data || [];
    }
  });

  const { data: affiliatedPeople, isLoading: loadingPeople } = useQuery({
    queryKey: ['org-people', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('people')
        .select('id, first_name, last_name, email')
        .eq('organization_id', id)
        .order('last_name', { ascending: true });
      if (error) throw error;
      return data as Person[];
    }
  });

  const involvementHistory = useMemo(() => {
    const list: Involvement[] = [];
    engagements?.forEach((eo: any) => {
      list.push({
        id: `eng-${eo.engagement_id}-${eo.role_id}`,
        program: eo.engagements?.programs?.name || '—',
        engagement: eo.engagements?.name || '—',
        session: null,
        role: eo.organization_roles?.label || 'Partner',
        date: null,
        type: 'engagement',
        linkId: eo.engagement_id
      });
    });
    hostedSessions?.forEach((s: any) => {
      list.push({
        id: `sess-${s.id}`,
        program: s.engagements?.programs?.name || '—',
        engagement: s.engagements?.name || '—',
        session: s.name || 'Operational Session',
        role: 'Host Site',
        date: s.session_datetime,
        type: 'session',
        linkId: s.id
      });
    });
    return list.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [engagements, hostedSessions]);

  if (loadingOrg) return <div className="p-20 text-center uppercase font-black text-slate-300 animate-pulse tracking-widest text-[10px]">Resolving Organizational Identity...</div>;
  if (!org) return <div className="p-20 text-center text-rose-500 font-black uppercase text-[10px]">Registry Error: Organization Not Found</div>;

  return (
    <div className="space-y-10 animate-in fade-in duration-500 pb-24 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate('/organizations')} className="p-3 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all text-slate-400 hover:text-slate-900">
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-slate-900 text-[#FFDB00] rounded-lg flex items-center justify-center font-black text-3xl shadow-xl shrink-0">
              <Building2 size={36} />
            </div>
            <div>
              <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] leading-none mb-1">{org.organization_types?.label || 'General Organization'}</p>
              <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tight leading-none">{org.name}</h1>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <Button variant="outline" onClick={() => setIsEditModalOpen(true)} icon={<Edit3 size={16} />}>Modify</Button>
           <button 
            onClick={() => setIsDeleteDialogOpen(true)}
            className="p-3 text-rose-400 bg-white border border-slate-200 rounded-lg hover:bg-rose-50 hover:text-rose-600 transition-all shadow-sm"
           >
             <Trash2 size={20} />
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full min-h-[500px]">
            <div className="p-6 bg-slate-50/50 border-b border-slate-100">
              <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">Organization Profile</h3>
            </div>
            
            <div className="p-8 space-y-10 flex-1">
              <div className="space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2"><Tag size={12} className="text-slate-400" /> Organization Type</p>
                <p className="text-[11px] font-bold text-slate-900 uppercase tracking-tight">{org.organization_types?.label || 'General'}</p>
              </div>

              <div className="space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2"><Globe size={12} className="text-slate-400" /> Website</p>
                {org.website ? (
                  <a href={org.website} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-blue-600 uppercase tracking-tight flex items-center gap-2 hover:underline">
                    {org.website.replace(/^https?:\/\//, '')} <ExternalLink size={12} />
                  </a>
                ) : <p className="text-[11px] font-bold text-slate-300 uppercase tracking-tight">—</p>}
              </div>

              <div className="space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2"><MapPin size={12} className="text-slate-400" /> Address</p>
                <div className="text-[11px] font-bold text-slate-900 uppercase tracking-tight flex items-start gap-2 leading-relaxed">
                  <div className="flex flex-col gap-0.5">
                    {formatAddressLines(org).length > 0 ? (
                      formatAddressLines(org).map((line, i) => <span key={i}>{line}</span>)
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 border-t border-slate-50 bg-slate-50/20">
              <div className="space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2"><StickyNote size={12} /> Notes</p>
                <div className="text-[11px] font-medium text-slate-600 leading-relaxed min-h-[120px] whitespace-pre-wrap italic bg-white p-4 rounded border border-slate-100 shadow-inner">
                  {org.notes || 'No administrative notes recorded.'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[600px]">
            <div className="bg-white border-b border-slate-100 px-8 flex items-center gap-12">
               <button 
                onClick={() => setActiveTab('involvement')}
                className={`py-6 text-[11px] font-black uppercase tracking-[0.2em] transition-all border-b-4 ${activeTab === 'involvement' ? 'border-blue-600 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
               >
                 Involvement <span className="text-slate-300 ml-1">({involvementHistory.length})</span>
               </button>
               <button 
                onClick={() => setActiveTab('people')}
                className={`py-6 text-[11px] font-black uppercase tracking-[0.2em] transition-all border-b-4 ${activeTab === 'people' ? 'border-blue-600 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
               >
                 Linked People <span className="text-slate-300 ml-1">({affiliatedPeople?.length || 0})</span>
               </button>
            </div>

            <div className="overflow-x-auto flex-1">
              {activeTab === 'involvement' ? (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-50 bg-slate-50/50 text-[8px] font-black uppercase text-slate-400 tracking-widest">
                      <th className="px-8 py-4">Program / Engagement Context</th>
                      <th className="px-8 py-4 text-center">Reference Role</th>
                      <th className="px-8 py-4 text-right">Log Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {involvementHistory.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-8 py-32 text-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">Zero Involvement Records Discovered</td>
                      </tr>
                    ) : (
                      involvementHistory.map((inv) => (
                        <tr 
                          key={inv.id} 
                          onClick={() => navigate(inv.type === 'engagement' ? `/engagements/${inv.linkId}` : `/sessions/${inv.linkId}`)}
                          className="hover:bg-slate-50 transition-colors group cursor-pointer"
                        >
                          <td className="px-8 py-5">
                            <p className="text-[11px] font-black text-slate-900 uppercase tracking-tight leading-none mb-1">{inv.program}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{inv.engagement}</p>
                          </td>
                          <td className="px-8 py-5 text-center">
                            <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded border ${inv.role.includes('Host') ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                              {inv.role}
                            </span>
                          </td>
                          <td className="px-8 py-5 text-right">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{formatDateSafe(inv.date)}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-50 bg-slate-50/50 text-[8px] font-black uppercase text-slate-400 tracking-widest">
                      <th className="px-8 py-4">Name</th>
                      <th className="px-8 py-4 text-right">View Profile</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {loadingPeople ? (
                      [1, 2, 3, 4].map(i => <tr key={i} className="animate-pulse"><td colSpan={3} className="px-8 py-6"><div className="h-4 bg-slate-50 rounded w-full" /></td></tr>)
                    ) : (affiliatedPeople || []).length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-8 py-32 text-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">No personnel currently associated with this organization</td>
                      </tr>
                    ) : (
                      (affiliatedPeople || []).map(person => (
                        <tr 
                          key={person.id} 
                          onClick={() => navigate(`/people/${person.id}`)}
                          className="hover:bg-slate-50 transition-colors group cursor-pointer"
                        >
                          <td className="px-8 py-4">
                             <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-sm bg-slate-900 text-[#FFDB00] flex items-center justify-center font-black text-[10px] uppercase shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-all">
                                  {person.first_name[0]}{person.last_name[0]}
                                </div>
                                <span className="text-[11px] font-black uppercase text-slate-900 tracking-tight">{person.first_name} {person.last_name}</span>
                             </div>
                          </td>
                          <td className="px-8 py-4 text-right">
                            <ChevronRight size={14} className="text-slate-200 inline-block group-hover:text-slate-900 group-hover:translate-x-1 transition-all" />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {isEditModalOpen && <OrganizationFormModal organization={org} onClose={() => setIsEditModalOpen(false)} />}
      
      {isDeleteDialogOpen && (
        <OrganizationDeleteDialog 
          orgId={org.id}
          orgName={org.name}
          onClose={() => setIsDeleteDialogOpen(false)}
          onSuccess={() => navigate('/organizations')}
        />
      )}
    </div>
  );
};

export default OrganizationDetail;